import { Link } from 'react-router-dom';
import EnhancedSEO from '@/components/seo/EnhancedSEO';
import { OG_IMAGES } from '@/constants/seoImages';
import { Button } from '@/components/ui/button';
import { useEarlyAdopter } from '@/contexts/EarlyAdopterContext';
import { 
  UsersIcon, 
  CalendarIcon, 
  ChatBubbleLeftRightIcon, 
  EyeIcon, 
  BellAlertIcon, 
  CheckCircleIcon, 
  ArrowRightIcon,
  HeartIcon,
  ShieldCheckIcon,
  SparklesIcon,
  HomeIcon,
  ClockIcon,
  UserGroupIcon,
  LifebuoyIcon
} from '@heroicons/react/24/outline';
import { Target } from 'lucide-react';
import familyThrivingHero from '@/assets/family-thriving-hero-watercolor.jpg';
import avatarWoman1 from '@/assets/avatar-woman-1.jpg';
import avatarMan1 from '@/assets/avatar-man-1.jpg';
import avatarWoman2 from '@/assets/avatar-woman-2.jpg';
import FamilySystemComparison from '@/components/family/FamilySystemComparison';

const FamilyMembers = () => {
  const { openModal } = useEarlyAdopter();
  const howItHelpsFeatures = [
    {
      title: "Shared Daily Signals",
      description: "See simple indicators that routines are happening and life at home is steady, without needing to interrupt or check in constantly.",
      icon: EyeIcon,
      accentColor: "primary"
    },
    {
      title: "Wellness & Medication Awareness",
      description: "Stay informed about patterns and changes over time, not every detail of every day.",
      icon: HeartIcon,
      accentColor: "emerald"
    },
    {
      title: "Coordinated Alerts",
      description: "When something shifts, the right people are notified at the right time, no group texts, no confusion.",
      icon: BellAlertIcon,
      accentColor: "amber"
    },
    {
      title: "Support Without Pressure",
      description: "The Family plan keeps families connected and helpful without turning everyday life into a project.",
      icon: ShieldCheckIcon,
      accentColor: "primary"
    }
  ];


  return (
    <>
      <EnhancedSEO 
        title="Family – Family Coordination"
        description="Gain a clear, shared view of how things are going, so you can stay connected, informed, and confident as your loved one thrives at home."
        canonical="https://seniorthrive.com/thrivefamily"
        image={OG_IMAGES.thriveFamily}
      />

      {/* HERO SECTION - Full-width image like homepage */}
      <section className="relative w-full min-h-[75vh] lg:min-h-[85vh] overflow-hidden">
        {/* Full-width background image - positioned to show the older woman */}
        <div 
          className="absolute inset-0 bg-cover bg-[75%_center]"
          style={{ backgroundImage: `url(${familyThrivingHero})` }}
        />
        
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-transparent" />
        
        {/* Content positioned on the left side with readable backdrop */}
        <div className="relative h-full min-h-[75vh] lg:min-h-[85vh] flex items-center">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="max-w-2xl pt-24 md:pt-32 pb-20 md:pb-28">
              {/* Semi-transparent backdrop for text readability */}
              <div className="bg-black/50 backdrop-blur-sm rounded-md p-8 md:p-10 text-left">
                {/* Badge */}
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20 w-fit mb-6">
                  <SparklesIcon className="w-4 h-4 text-white" />
                  <span className="text-sm font-bold text-white uppercase tracking-wider">Family</span>
                </div>
                
                {/* Main headline */}
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight leading-[1.1] drop-shadow-lg">
                  The Family Support System for{' '}
                  <span className="text-primary-foreground">Thriving at Home</span>
                </h1>
                
                {/* Subheadline */}
                <p className="text-lg sm:text-xl lg:text-2xl text-white/90 mb-6 leading-relaxed drop-shadow-md">
                  Gain a clear, shared view of how things are going, so you can stay connected, informed, and confident as your loved one thrives at home.
                </p>
                
                {/* Trust line */}
                <div className="flex items-center gap-3 text-white/80 mb-6">
                  <ShieldCheckIcon className="w-5 h-5" />
                  <span>Built to reduce worry, not add responsibility.</span>
                </div>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <Button size="lg" className="text-lg px-8 py-6 rounded-sm font-bold min-h-[56px] bg-primary hover:bg-black/50 text-primary-foreground shadow-overlay" onClick={() => openModal('Family Members Hero - Get Early Access')}>
                      Get Early Access
                      <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </Button>
                  <Link to="/home-safety-quiz">
                    <Button size="lg" variant="outline" className="text-lg px-8 py-6 rounded-sm font-bold min-h-[56px] bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm">
                      Take the Safety Check-In
                    </Button>
                  </Link>
                </div>

                {/* Social proof */}
                <div className="flex items-center gap-4 text-sm text-white/70">
                  <div className="flex -space-x-2">
                    <img src={avatarWoman1} alt="Family plan user" className="w-8 h-8 rounded-full border-2 border-white/30 object-cover" />
                    <img src={avatarMan1} alt="Family plan user" className="w-8 h-8 rounded-full border-2 border-white/30 object-cover" />
                    <img src={avatarWoman2} alt="Family plan user" className="w-8 h-8 rounded-full border-2 border-white/30 object-cover" />
                  </div>
                  <span>Trusted by families who want clarity, connection, and peace of mind.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: WHY FAMILIES NEED A SYSTEM */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-secondary/20 via-background to-accent/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
                Why Families Need a System
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                When support is spread across texts, calls, and memory, everyone ends up guessing. That guessing creates stress, miscommunication, and unnecessary worry.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-primary/5 to-accent/10 rounded-md p-8 lg:p-10 shadow-floating mb-10">
              <p className="text-xl lg:text-2xl text-foreground text-center leading-relaxed">
                A system replaces that chaos with steady signals, so you know when things are going well and when your help actually matters.
              </p>
            </div>
            
            {/* Supporting Points */}
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: ChatBubbleLeftRightIcon, text: "End the \"just checking in\" phone call", accent: "primary" },
                { icon: ArrowRightIcon, text: "Shift from reacting to problems to supporting daily momentum", accent: "emerald" },
                { icon: UsersIcon, text: "Keep siblings, spouses, and extended family aligned without friction", accent: "amber" }
              ].map((point, i) => {
                const colors = {
                  primary: { bg: 'bg-primary/10', icon: 'text-primary', border: 'border-primary/10 hover:border-primary/25' },
                  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', icon: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200/50 hover:border-emerald-300' },
                  amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', icon: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200/50 hover:border-amber-300' }
                };
                const style = colors[point.accent as keyof typeof colors];
                return (
                  <div key={i} className={`flex items-start gap-4 p-5 rounded-md bg-card shadow-raised hover:-translate-y-0.5 transition-all duration-300`}>
                    <div className={`w-10 h-10 ${style.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <point.icon className={`w-5 h-5 ${style.icon}`} />
                    </div>
                    <p className="text-foreground leading-relaxed">{point.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: WHAT CHANGES - COMPARISON */}
      <FamilySystemComparison />

      {/* SECTION 4: HOW THRIVEFAMILY HELPS */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-accent/10 via-background to-secondary/15">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                How the Family Plan Helps
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Practical, not heavy. Connected, not controlling.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {howItHelpsFeatures.map((feature, idx) => {
                const accentStyles = {
                  primary: { bg: 'bg-primary/10', icon: 'text-primary', border: 'border-primary/15 hover:border-primary/30', shadow: 'hover:shadow-primary/5' },
                  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', icon: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200/50 hover:border-emerald-400/50', shadow: 'hover:shadow-emerald-500/5' },
                  amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', icon: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200/50 hover:border-amber-400/50', shadow: 'hover:shadow-amber-500/5' }
                };
                const style = accentStyles[feature.accentColor as keyof typeof accentStyles] || accentStyles.primary;
                
                return (
                  <div key={idx} className={`group p-8 rounded-md bg-card shadow-raised transition-all duration-300 hover:-translate-y-0.5 hover:shadow-floating`}>
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-12 h-12 ${style.bg} rounded-md flex items-center justify-center flex-shrink-0`}>
                        <feature.icon className={`w-6 h-6 ${style.icon}`} />
                      </div>
                      <h3 className="text-xl font-bold text-foreground">{feature.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: TOOLS FOR THE HARD PARTS */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-primary/5 via-secondary/10 to-accent/15">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Tools for the Hard Parts
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                Caring for a parent is emotional. The Family plan gives you practical tools for the conversations and moments that matter most.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {[
                {
                  icon: ChatBubbleLeftRightIcon,
                  title: "Conversation Guide",
                  description: "Not sure how to bring up safety, driving, or health changes? Step-by-step guides help you start difficult conversations with empathy and confidence.",
                  accent: "primary"
                },
                {
                  icon: ClockIcon,
                  title: "Meaningful Call Prompts",
                  description: "Move beyond \"How are you?\" with conversation starters that spark real connection, about memories, interests, and daily wins.",
                  accent: "emerald"
                },
                {
                  icon: SparklesIcon,
                  title: "Memory Spark",
                  description: "Simple activities you can do together, in person or over the phone, to strengthen your bond and create new shared moments.",
                  accent: "amber"
                },
                {
                  icon: UserGroupIcon,
                  title: "Family Harmony Guide",
                  description: "Navigating caregiving between siblings is hard. Tools to clarify who does what, balance the load, and reduce friction before it builds.",
                  accent: "primary"
                }
              ].map((tool, i) => {
                const colors = {
                  primary: { bg: 'bg-primary/10', icon: 'text-primary', border: 'border-primary/15 hover:border-primary/30' },
                  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', icon: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200/50 hover:border-emerald-400/50' },
                  amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', icon: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200/50 hover:border-amber-400/50' }
                };
                const style = colors[tool.accent as keyof typeof colors];
                return (
                  <div key={i} className={`group p-8 rounded-md bg-card shadow-raised transition-all duration-300 hover:-translate-y-0.5 hover:shadow-floating`}>
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-12 h-12 ${style.bg} rounded-md flex items-center justify-center flex-shrink-0`}>
                        <tool.icon className={`w-6 h-6 ${style.icon}`} />
                      </div>
                      <h3 className="text-xl font-bold text-foreground">{tool.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{tool.description}</p>
                  </div>
                );
              })}
            </div>

            {/* Emotional acknowledgment */}
            <div className="bg-gradient-to-br from-primary/5 to-accent/10 rounded-md p-8 lg:p-10 shadow-floating text-center">
              <p className="text-xl lg:text-2xl text-foreground leading-relaxed mb-4">
                If you are already doing everything you can, let SeniorThrive carry some of the weight.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed">
                You deserve support too. The Family plan helps you be present without burning out.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: EVERYTHING YOUR FAMILY GETS */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-background via-muted/20 to-secondary/15">
        <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">Everything Your Family Gets</h2>
              <p className="text-xl text-muted-foreground">Designed to support families without overwhelm</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: UsersIcon,
                  title: "The Older Adult + Up to 4 Family Members",
                  description: "Five people total, siblings, spouses, extended family, all aligned.",
                  accent: "primary"
                },
                {
                  icon: Target,
                  title: "ThriveScore at a Glance",
                  description: "One number that shows when things are steady and when something may need attention.",
                  accent: "amber"
                },
                {
                  icon: HomeIcon,
                  title: "Shared Home Safety View",
                  description: "See room-by-room safety status and improvements, no guessing.",
                  accent: "emerald"
                },
                {
                  icon: CalendarIcon,
                  title: "Shared Family Calendar",
                  description: "Appointments, plans, and important moments visible to everyone who needs to know.",
                  accent: "primary"
                },
                {
                  icon: ChatBubbleLeftRightIcon,
                  title: "Family Communication Hub",
                  description: "One place for updates and conversations, no more lost texts or group chat chaos.",
                  accent: "amber"
                },
                {
                  icon: LifebuoyIcon,
                  title: "7 AI Companions",
                  description: "On-demand guidance for health, medications, cooking, care coordination, and more.",
                  accent: "emerald"
                }
              ].map((feature, i) => {
                const colors = {
                  primary: { bg: 'bg-primary/10', icon: 'text-primary', border: 'border-primary/10 hover:border-primary/25' },
                  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', icon: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200/40 hover:border-emerald-300' },
                  amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', icon: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200/40 hover:border-amber-300' }
                };
                const style = colors[feature.accent as keyof typeof colors];
                return (
                  <div key={i} className={`bg-card p-6 rounded-md shadow-raised hover:-translate-y-0.5 transition-all duration-300`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 ${style.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <feature.icon className={`w-5 h-5 ${style.icon}`} />
                      </div>
                      <h3 className="font-bold text-foreground">{feature.title}</h3>
                    </div>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </div>
                );
              })}
            </div>

            {/* Grows with you */}
            <div className="mt-16 bg-gradient-to-br from-primary/5 to-accent/10 rounded-lg p-8 lg:p-10">
              <h3 className="text-2xl font-bold text-foreground text-center mb-3">
                It Grows With Your Family's Needs
              </h3>
              <p className="text-lg text-muted-foreground text-center leading-relaxed max-w-2xl mx-auto mb-8">
                Start with the Family plan. If your loved one's needs change, you never have to start over.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    title: 'Family',
                    description: 'Shared visibility, daily signals, family coordination, and hands-on care tools, everything you need from day one.',
                    tag: 'You are here',
                    active: true,
                    link: null,
                  },
                  {
                    title: 'Care Team',
                    description: 'Bring in professional caregivers with shift documentation, handoffs, and family communication, all in one system.',
                    tag: '+$15/caregiver/month',
                    active: false,
                    link: '/caregiver-coordination',
                  },
                ].map((tier, i) => {
                  const card = (
                    <div key={i} className={`p-6 rounded-md ${tier.active ? 'shadow-floating' : 'bg-card/80 shadow-raised hover:-translate-y-0.5 transition-all cursor-pointer'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <h4 className="font-bold text-foreground">{tier.title}</h4>
                        <span className={`text-sm px-2 py-0.5 rounded-full ${tier.active ? 'text-primary font-bold' : 'bg-muted text-muted-foreground'}`}>
                          {tier.tag}
                        </span>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">{tier.description}</p>
                    </div>
                  );
                  return tier.link ? <Link key={i} to={tier.link}>{card}</Link> : <div key={i}>{card}</div>;
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS, Purchase Flow */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-background via-secondary/15 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                How Getting Started Works
              </h2>
              <p className="text-xl text-muted-foreground">
                No risk. No pressure. No billing until everyone is on board.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {[
                {
                  step: '1',
                  title: 'You explore free',
                  description: 'Sign up, invite siblings, and set up your family dashboard. Take your time, there is no billing at this stage.',
                },
                {
                  step: '2',
                  title: 'Your loved one joins',
                  description: 'When they are ready, they accept your invitation and activate their own account with full Essential access.',
                },
                {
                  step: '3',
                  title: 'Billing begins',
                  description: 'Only after your loved one is active does the subscription start. You are never charged until everyone is on board.',
                },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-14 h-14 bg-primary text-primary-foreground rounded-md flex items-center justify-center mx-auto mb-4 font-bold text-2xl shadow-overlay">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>

            {/* Conversation guide link */}
            <div className="bg-gradient-to-br from-primary/5 to-accent/10 rounded-md p-8 shadow-raised text-center">
              <h3 className="text-xl font-bold text-foreground mb-3">
                Not sure how to bring it up?
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-4 max-w-2xl mx-auto">
                Introducing safety tools to a parent can feel awkward. We created a step-by-step
                guide with real conversation starters that respect their independence.
              </p>
              <Link to="/conversation-guide">
                <Button variant="outline" size="lg" className="font-bold min-h-[48px]">
                  Read the Conversation Guide
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4">
          <div className="relative overflow-hidden rounded-lg max-w-5xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-glow to-primary/80"></div>
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-radial from-primary-foreground/20 to-transparent rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-gradient-radial from-primary-foreground/15 to-transparent rounded-full blur-2xl"></div>
            </div>
            
            <div className="relative text-center p-12 lg:p-16 text-primary-foreground">
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold mb-6">
                Stay Connected. Stay Informed.<br />
                <span className="opacity-90">Support Thriving at Home.</span>
              </h2>
              <p className="text-xl mb-8 opacity-95 max-w-2xl mx-auto leading-relaxed">
                The Family plan helps you support the life your loved one is actively living, with clarity, calm, and the right tools when they matter.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  variant="secondary"
                  className="text-lg px-10 py-6 min-h-[56px] bg-white text-primary hover:bg-white/95 shadow-overlay transition-all group font-bold"
                  onClick={() => openModal('Family Members CTA - Get Early Access')}
                >
                    Get Early Access
                    <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
              
              <p className="text-sm opacity-80 mt-6">
                Trusted by families who want peace of mind without hovering.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FamilyMembers;
